import * as XLSX from 'xlsx';

export interface NYCRenovationTier {
  category: string;
  tier: string;
  lowCost: number;
  highCost: number;
  averageCost: number;
  unit: string;
  description: string;
}

export interface NYCCostData {
  tiers: NYCRenovationTier[];
  categories: string[];
  tierNames: string[];
}

// Parse the NYC Home Renovation Costs Excel file
export const parseNYCCostData = (workbook: XLSX.WorkBook): NYCCostData => {
  const tiers: NYCRenovationTier[] = [];
  const categories: string[] = [];
  const tierNames: string[] = [];
  
  // Get the first worksheet
  const sheetName = workbook.SheetNames[0];
  const worksheet = workbook.Sheets[sheetName];
  
  // Convert to JSON with header row
  const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: '' });
  
  let currentCategory = '';
  let headers: string[] = [];
  
  for (let i = 0; i < jsonData.length; i++) {
    const row = jsonData[i] as any[];
    
    // Skip empty rows
    if (!row || row.length === 0) continue;
    
    // Look for headers row (usually contains tier names)
    if (i === 0 || (row[0] && typeof row[0] === 'string' && 
        (row[0].toLowerCase().includes('tier') || row[0].toLowerCase().includes('budget') || 
         row[0].toLowerCase().includes('cost') || row[0].toLowerCase().includes('level')))) {
      headers = row.map(cell => String(cell).trim()).filter(cell => cell && cell !== '');
      continue;
    }
    
    // Look for category headers (usually in first column)
    if (row[0] && typeof row[0] === 'string' && row[0].trim() && 
        !row[0].toLowerCase().includes('tier') && !row[0].toLowerCase().includes('budget') &&
        !row[0].toLowerCase().includes('cost') && !row[0].toLowerCase().includes('level') &&
        !row[0].toLowerCase().includes('description') && !row[0].toLowerCase().includes('unit')) {
      currentCategory = row[0].trim();
      if (!categories.includes(currentCategory)) {
        categories.push(currentCategory);
      }
      continue;
    }
    
    // Look for data rows (should have description and cost data)
    if (row[0] && typeof row[0] === 'string' && row[0].trim() && 
        row[0] !== currentCategory && !headers.includes(row[0])) {
      
      const description = row[0].trim();
      const unit = row[1] ? String(row[1]).trim() : 'SF';
      
      // Extract cost data from remaining columns
      const costData: number[] = [];
      for (let j = 2; j < row.length; j++) {
        if (row[j] && typeof row[j] === 'string') {
          const costMatch = String(row[j]).match(/\$?([\d,]+\.?\d*)/);
          if (costMatch) {
            costData.push(parseFloat(costMatch[1].replace(/,/g, '')));
          }
        } else if (typeof row[j] === 'number') {
          costData.push(row[j]);
        }
      }
      
      // Create tiers based on available cost data
      if (costData.length >= 2) {
        const tierCount = Math.min(costData.length, headers.length - 2); // Subtract description and unit columns
        
        for (let k = 0; k < tierCount; k++) {
          const tierName = headers[k + 2] || `Tier ${k + 1}`;
          if (!tierNames.includes(tierName)) {
            tierNames.push(tierName);
          }
          
          const lowCost = costData[k] || 0;
          const highCost = costData[k] ? costData[k] * 1.2 : 0; // Assume 20% high end
          const averageCost = (lowCost + highCost) / 2;
          
          if (lowCost > 0) {
            tiers.push({
              category: currentCategory,
              tier: tierName,
              lowCost,
              highCost,
              averageCost,
              unit,
              description
            });
          }
        }
      }
    }
  }
  
  return { tiers, categories, tierNames };
};

// Load NYC cost data from public folder
export const loadNYCCostData = async (): Promise<NYCCostData> => {
  try {
    const response = await fetch('/Assets/NYC_Home_Renovation_Costs_Tiers.xlsx');
    const arrayBuffer = await response.arrayBuffer();
    const workbook = XLSX.read(arrayBuffer, { type: 'array' });
    
    return parseNYCCostData(workbook);
  } catch (error) {
    console.error('Error loading NYC cost data:', error);
    return { tiers: [], categories: [], tierNames: [] };
  }
};

// Convert NYC cost data to renovation estimator format
export const convertNYCToLaborUnitCosts = (nycData: NYCCostData): Record<string, { unit: string; price: number; laborPercent: number; materialPercent: number }> => {
  const laborUnitCosts: Record<string, { unit: string; price: number; laborPercent: number; materialPercent: number }> = {};
  
  nycData.tiers.forEach(tier => {
    const key = `${tier.description} (${tier.tier})`.replace(/[^a-zA-Z0-9\s]/g, '').trim();
    
    // Estimate labor/material split based on category
    let laborPercent = 50; // Default 50/50 split
    let materialPercent = 50;
    
    if (tier.category.toLowerCase().includes('labor') || tier.category.toLowerCase().includes('installation')) {
      laborPercent = 80;
      materialPercent = 20;
    } else if (tier.category.toLowerCase().includes('material') || tier.category.toLowerCase().includes('finish')) {
      laborPercent = 20;
      materialPercent = 80;
    } else if (tier.category.toLowerCase().includes('demolition')) {
      laborPercent = 90;
      materialPercent = 10;
    } else if (tier.category.toLowerCase().includes('electrical') || tier.category.toLowerCase().includes('plumbing')) {
      laborPercent = 70;
      materialPercent = 30;
    }
    
    laborUnitCosts[key] = {
      unit: tier.unit,
      price: tier.averageCost,
      laborPercent,
      materialPercent
    };
  });
  
  return laborUnitCosts;
};

// Get cost range for a specific renovation type and finish level
export const getNYCCostRange = (nycData: NYCCostData, renovationType: string, finishLevel: string): { low: number; high: number; average: number } => {
  const relevantTiers = nycData.tiers.filter(tier => 
    tier.category.toLowerCase().includes(renovationType.toLowerCase()) ||
    tier.description.toLowerCase().includes(renovationType.toLowerCase())
  );
  
  if (relevantTiers.length === 0) {
    return { low: 0, high: 0, average: 0 };
  }
  
  const lowCosts = relevantTiers.map(tier => tier.lowCost);
  const highCosts = relevantTiers.map(tier => tier.highCost);
  const averageCosts = relevantTiers.map(tier => tier.averageCost);
  
  return {
    low: Math.min(...lowCosts),
    high: Math.max(...highCosts),
    average: averageCosts.reduce((sum, cost) => sum + cost, 0) / averageCosts.length
  };
};
